import { ArrowLeft } from '@mui/icons-material'
import type { BoxProps } from '@mui/material'
import { Box, Button } from '@mui/material'
import { graphHooks } from '@tunasong/graph-lib/react'
import { ArrowRight } from '@tunasong/icons'
import type { FilterFunc, NavigationEntity } from '@tunasong/models'
import { isPersistedEntity, matchAny } from '@tunasong/models'
import type { Entity, Persisted } from '@tunasong/schemas'
import { isAudio, isFolderContainer, isMedia, isSong } from '@tunasong/schemas'
import { useCallback, useState } from 'react'
import invariant from 'tiny-invariant'
import { DndList } from '../list/dnd-list.js'
import type { NavFn } from '../navigation/navigate.js'
import { EntityTree } from './entity-tree.js'

export interface TransferListProps extends Omit<BoxProps, 'onChange'> {
  parent?: Persisted<Entity>
  list: Persisted<Entity>[]
  filter?: FilterFunc
  onChange(list: Persisted<Entity>[]): void
}

const defaultFilter = matchAny(isSong, isAudio, isFolderContainer)

export function TransferList(props: TransferListProps) {
  const { list = [], filter: filterFunc = defaultFilter, onChange, ...restProps } = props
  const { entities: rootEntities } = graphHooks.useEntities({ root: true, filter: filterFunc })
  const [selectedList, setCheckedList] = useState<Persisted<Entity>[]>([])
  const [selected, setSelected] = useState<Persisted<Entity>>()

  const handleAdd = useCallback(() => {
    if (!(selected && list)) {
      return
    }
    onChange([...list, selected])
    setSelected(undefined)
  }, [list, onChange, selected])

  const handleRemove = useCallback(() => {
    const c = list.filter(s => !selectedList.includes(s))
    setCheckedList([])
    onChange(c)
  }, [list, onChange, selectedList])

  const handleSelectTree = useCallback((value: Persisted<Entity>) => {
    const val = isMedia(value) ? value : undefined
    setSelected(val)
  }, [])

  const handleSelectList: NavFn = useCallback((value?: NavigationEntity) => {
    invariant(isPersistedEntity(value))
    setCheckedList([value])
  }, [])

  const notDropTarget = useCallback(() => false, [])

  return (
    <Box
      {...restProps}
      sx={{
        flex: 1,
        display: 'flex',
        maxHeight: '100%',
        overflow: 'hidden',
        justifyContent: 'space-between',
      }}
    >
      <EntityTree rootEntities={rootEntities} selected={selected} filter={filterFunc} onSelect={handleSelectTree} />
      <Box display="flex" flexDirection="column">
        <Button
          variant="outlined"
          size="small"
          onClick={handleAdd}
          disabled={!selected}
          aria-label="move selected right"
          sx={{ margin: theme => theme.spacing(0.5, 0) }}
        >
          {/* Greater than sign unicode */}
          <ArrowRight />
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={handleRemove}
          disabled={selectedList.length === 0}
          aria-label="move selected left"
          sx={{ margin: theme => theme.spacing(0.5, 0) }}
        >
          <ArrowLeft />
        </Button>
      </Box>

      {/* Use DnDList for DnD sorting */}
      <DndList
        component="div"
        sx={{ minWidth: 200 }}
        entities={list}
        isDropTarget={notDropTarget}
        sort={false}
        actions={false}
        onChange={onChange}
        selected={selectedList}
        onOpen={handleSelectList}
        navigate={false}
      />
    </Box>
  )
}
